import { Injectable, OnDestroy } from '@angular/core';
import { ApiService } from '../api/api.service';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import {
  GenieGames,
  IJourney,
  IQuestion,
  QuizQuestion,
  IJumble,
  IJourneyLanguage,
  CortevaParticipant,
} from './journey.interface';
import * as moment from 'moment';
import { Observable, tap, map, Subject, takeUntil } from 'rxjs';
import {
  REFRESH_TIME_IN_MINUTES,
  environment,
} from 'src/environments/environment';
import { IListParams, IStoreProps } from '../common.interface';
import { AuthCoreService } from '../auth-core/auth-core.service';
import {
  IJumblePayload,
  IJumbleUpdatePayload,
} from 'src/app/home/journey/journey.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';

const STORE_NAME = 'journeys';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: environment.cacheDbName,
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },

  withProps<IStoreProps>({}),
  withEntities<IJourney, '_id'>({ idKey: '_id' }),
  withProps<{ companyId: string }>({ companyId: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class JourneyCoreService implements OnDestroy {
  private _unsubscribe$ = new Subject<void>();
  private _loading = false;

  private _isCrosswordsGame(format?: GenieGames | string | null): boolean {
    return format === GenieGames.CROSSWORDSGAME || format === 'CrosswordsGame';
  }

  private _isWordsPuzzleGame(format?: GenieGames | string | null): boolean {
    return (
      format === GenieGames.WORDPUZZLESGAME || format === 'WordsPuzzleGame'
    );
  }

  private _mapJumblePath(
    path: string,
    format?: GenieGames | string | null,
  ): string {
    if (this._isWordsPuzzleGame(format)) {
      return path
        .replace('/genie/jumble/', '/genie/wordsPuzzle/')
        .replace('generateJumbles', 'generatePuzzles')
        .replace('generateJumble', 'generatePuzzle')
        .replace('deleteJumble', 'deletePuzzle')
        .replace('addJumbles', 'addPuzzles')
        .replace('editJumble', 'editPuzzle')
        .replace('reorderJumbles', 'reorderPuzzles');
    }

    if (!this._isCrosswordsGame(format)) {
      return path;
    }

    return path
      .replace(/Jumbles/g, 'Crosswords')
      .replace(/Jumble/g, 'Crossword')
      .replace(/jumbles/g, 'crosswords')
      .replace(/jumble/g, 'crossword');
  }

  /**
   * @name journeyList$
   * @description emits stored list of the companies
   */

  journeyList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );

  public get loading(): boolean {
    return this._loading;
  }

  constructor(
    private _api: ApiService,
    private _http: HttpClient,
    private _authCoreService: AuthCoreService,
  ) {}

  public getJourneys(params?: IListParams): void {
    this._loading = true;
    this._api
      .get<IJourneyListResponse>(
        `/genie/journey/list/${AuthCoreService.companyUser?.company._id}`,
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe({
        next: (data: IJourneyListResponse) => {
          store.update(
            setEntities(data.list),
            setProp('lastRefreshed', new Date()),
          );
          this._loading = false;
        },
      });
  }

  public addJourney(data: Partial<IJourney>): Observable<any> {
    this._loading = true;
    return this._api.post<IJourney>('/genie/journey', data).pipe(
      tap((journey: IJourney) => {
        store.update(
          addEntities(journey),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public getJourney(id: string): Observable<IJourney> {
    this._loading = true;
    return this._api.get<IJourney>(`/genie/journey/${id}`).pipe(
      tap((journey: IJourney) => {
        store.update(updateEntities(journey['_id'], journey));
        this._loading = false;
      }),
    );
  }

  public getJourneyLanguages(format: GenieGames): Observable<IJourneyLanguage> {
    this._loading = true;
    return this._api.get<IJourneyLanguage>(`/genie-language/${format}`).pipe(
      tap((journey: IJourneyLanguage) => {
        this._loading = false;
        return journey;
      }),
    );
  }


  public getCortevaParticipants(journeyId: string): Observable<CortevaParticipant[]> {
    this._loading = true;
    return this._api.get<CortevaParticipant[]>(`/genie/journey/${journeyId}/participants`).pipe(
      map((participants: CortevaParticipant[]) => {
        this._loading = false;
        return participants; 
      }),
    );
  }

  public uploadCortevaParticipants(journeyId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post(
      `${environment.serverUrl}/genie/journey/${journeyId}/participants/upload`, 
      formData,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  public updateJourney(
    id: string,
    journey: Partial<IJourney>,
  ): Observable<IJourney> {
    this._loading = true;
    if (!journey.contentFiles) {
      delete journey.contentFiles;
    }
    if (!journey.textContent) {
      delete journey.textContent;
    }
    if (!journey.gameFormat) {
      delete journey.gameFormat;
    }
    if (!journey.topic) {
      delete journey.topic;
    }
    return this._api.put<IJourney>(`/genie/journey/${id}`, journey).pipe(
      map((data: IJourney) => {
        store.update(updateEntities(id, data));
        this._loading = false;
        return data;
      }),
    );
  }

  public upsertQuestions(
    id: string,
    questions: QuizQuestion[] | QuizQuestion,
    excelUpload = false,
    index: number,
  ): Observable<IJourney> {
    this._loading = true;
    if (excelUpload) {
      return this._api
        .post<IJourney>(
          `/genie/journey/${id}/questions?excelUpload=${excelUpload}`,
          questions,
        )
        .pipe(
          map((data: IJourney) => {
            store.update(updateEntities(id, data));
            this._loading = false;
            return data;
          }),
        );
    } else {
      return this._api
        .post<IJourney>(
          `/genie/journey/${id}/questions?index=${index}`,
          questions,
        )
        .pipe(
          map((data: IJourney) => {
            store.update(updateEntities(id, data));
            this._loading = false;
            return data;
          }),
        );
    }
  }

  public updateQuestion(
    id: string,
    questionId: string,
    questions: QuizQuestion,
  ): Observable<IJourney> {
    this._loading = true;
    return this._api
      .put<IJourney>(`/genie/journey/${id}/${questionId}/questions`, questions)
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  public upsertJumbles(payload: {
    id: string;
    jumblesData: IJumblePayload[];
    excelUpload: boolean;
    index?: number;
    gameFormat?: GenieGames;
  }): Observable<IJourney> {
    const { id, excelUpload, jumblesData, index, gameFormat } = payload;
    this._loading = true;
    if (!excelUpload) {
      return this._api
        .post<IJourney>(
          this._mapJumblePath(
            `/genie/jumble/${id}/addJumbles?index=${index}&excelUpload=${excelUpload}`,
            gameFormat,
          ),
          jumblesData,
        )
        .pipe(
          map((data: IJourney) => {
            console.log('data: ', data);

            store.update(updateEntities(id, data));
            this._loading = false;
            return data;
          }),
        );
    } else {
      return this._api
        .post<IJourney>(
          this._mapJumblePath(
            `/genie/jumble/${id}/addJumbles?excelUpload=${excelUpload}`,
            gameFormat,
          ),
          jumblesData,
        )
        .pipe(
          map((data: IJourney) => {
            console.log('data: ', data);

            store.update(updateEntities(id, data));
            this._loading = false;
            return data;
          }),
        );
    }
  }

  public updateJumbles(payload: IJumbleUpdatePayload): Observable<IJourney> {
    const { _id, gameFormat } = payload;
    delete payload['gameFormat']
    this._loading = true;
    return this._api
      .put<IJourney>(
        this._mapJumblePath(`/genie/jumble/${_id}/editJumble`, gameFormat),
        payload,
      )
      .pipe(
        map((data: IJourney) => {
          console.log('data: ', data);

          store.update(updateEntities(_id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  public deleteQuestion(id: string, question: IQuestion): Observable<IJourney> {
    this._loading = true;
    return this._api
      .delete<IJourney>(`/genie/journey/${id}/question`, {
        _id: question._id,
        name: question.name,
      })
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  public deleteJumble(
    id: string,
    jumble: IJumble,
    gameFormat?: GenieGames,
  ): Observable<IJourney> {
    this._loading = true;
    return this._api
      .delete<IJourney>(
        this._mapJumblePath(`/genie/jumble/${id}/deleteJumble`, gameFormat),
        {
        _id: jumble._id,
        name: jumble.name,
      })
      .pipe(
        map((data: IJourney) => {
          console.log('data: ', data);

          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  public deleteJourney(data: any): Observable<any> {
    this._loading = true;
    return this._api.delete('/genie/journey', data).pipe(
      map(() => {
        store.update(
          deleteEntities(data['_id']),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  generateTextContent(
    id: string,
    topic: string,
    jumblesPayload?: {
      gameFormat: string;
      textContent: string;
    },
  ): Observable<IJourney> {
    this._loading = true;
    return this._api
      .post<IJourney>(
        jumblesPayload
          ? this._mapJumblePath(
              `/genie/jumble/${id}/generateJumbles`,
              jumblesPayload.gameFormat,
            )
          : `/genie/journey/${id}/generateContent`,
        { topic, ...jumblesPayload },
      )
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  reorderQuestions(id: string, questions: string[]): Observable<IJourney> {
    this._loading = true;
    return this._api
      .put<IJourney>(`/genie/journey/${id}/reoderQuestions`, questions)
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  reorderJumbles(
    id: string,
    jumbleIds: string[],
    gameFormat?: GenieGames,
  ): Observable<IJourney> {
    this._loading = true;
    return this._api
      .post<IJourney>(
        this._mapJumblePath(`/genie/jumble/${id}/reorderJumbles`, gameFormat),
        jumbleIds,
      )
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  generatePackage(id: string , selfContained?:boolean): Observable<IJourney> {
    this._loading = true;
    return this._api.get<IJourney>(selfContained ? `/genie/journey/${id}/package?selfContained=true` : `/genie/journey/${id}/package`).pipe(
      map((data: IJourney) => {
        store.update(updateEntities(id, data));
        this._loading = false;
        return data;
      }),
    );
  }

  uploadFile(payload: {
    type: 'pdf' | 'video';
    file: FormData;
    journeyId: string;
    language?:string
  }): Observable<any> {
    return this._http
      .post(
        `${environment.serverUrl}/genie/journey/${payload.journeyId}/${payload.type}/upload${payload.language ? `?language=${payload.language}`: ``}`,
        payload.file,
        {
          reportProgress: true,
          observe: 'events',
        },
      )
      .pipe(
        map((data: any) => {
          return data;
        }),
      );
  }

  uploadVideo(payload: {
    file: File;
    journeyId: string;
  }): Observable<any> {
    const formData = new FormData();
     formData.append('file',payload.file);
    return this._http
      .post(
        `${environment.serverUrl}/genie/journey/${payload.journeyId}/video/transcription`,
        formData,
        {
          reportProgress: true,
          observe: 'events',
        },
      )
      
  }
  deleteTutorialFile(payload: {
    type: 'pdf' | 'video';
    journeyId:string
    name:string
    url:string
    _id:string
    fileId?:string
  }): Observable<any> {
    return this._api
      .delete(`/genie/journey/${payload.journeyId}/${payload.type}`, {
        name:payload.name,
        url:payload.url,
        _id:payload._id,
       fileId : payload.fileId
      })
      .pipe(
        map((data: any) => {
          return data;
        }),
      );
  }

  uploadTutorialFile(payload: {
    gameType: 'Pdf' | 'Videos';
    gameFormat: GenieGames;
    fileId: string;
    learningGameId: string;
  }): Observable<any> {
    const isJumbleLike =
      payload.gameFormat === GenieGames.JUMBLESGAME ||
      payload.gameFormat === GenieGames.CROSSWORDSGAME ||
      payload.gameFormat === GenieGames.WORDPUZZLESGAME;
    return this._api
      .post(
        isJumbleLike
          ? this._mapJumblePath('/genie/jumble/tutorialFile', payload.gameFormat)
          : '/genie/journey/tutorialFile',
        payload,
        { responseType: 'blob' },
      )
      .pipe(
        map((data: any) => {
          return data;
        }),
      );
  }

  generateQuestions(
    id: string,
    textContent: string,
    topic: string,
    gameFormat: GenieGames,
    isJumbles: boolean,
  ): Observable<IJourney> {
    this._loading = true;
    return this._api
      .post<IJourney>(
        isJumbles
          ? this._mapJumblePath(`/genie/jumble/${id}/generateJumbles`, gameFormat)
          : `/genie/journey/${id}/generateQuestions`,
        {
          textContent,
          topic,
          gameFormat,
        },
      )
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }
  generateQuestionsFromContentFiles(
    id: string,
    isJumbles: boolean,
    gameFormat?: GenieGames,
  ): Observable<IJourney> {
    this._loading = true;
    return this._api
      .post<IJourney>(
        isJumbles
          ? this._mapJumblePath(
              `/genie/jumble/${id}/generateJumbles/fromContentFiles`,
              gameFormat,
            )
          : `/genie/journey/${id}/generateQuestions/fromContentFiles`,
        {},
      )
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  syncQuestions(id: string, questions: IQuestion[]): Observable<IJourney> {
    this._loading = true;
    return this._api
      .put<IJourney>(`/genie/journey/${id}/syncQuestions`, questions)
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(id, data));
          this._loading = false;
          return data;
        }),
      );
  }

  public getJourneyInfo(id: string): IJourney {
    const journey = store.query(getEntity(id));
    return journey as any;
  }

  generateSingleQuestion(payload: {
    journeyId: string;
    index: number;
    topic: string;
    gameFormat: string;
    questions: string[];
    isContentFile: boolean;
    count:number
    abortSignal: AbortSignal
  }): Observable<IJourney> {
    this._loading = true;
    return this._api
      .post<IJourney>(
        `/genie/journey/${payload.journeyId}/generateQuestion?index=${payload.index}&contentFile=${payload.isContentFile}&count=${payload.count}`,
        {
          questions: payload.questions,
          topic: payload.topic,
          gameFormat: payload.gameFormat,
        },
      )
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(payload.journeyId, data));
          this._loading = false;
          return data;
        }),
      );
  }

  generateSingleJumble(payload: {
    journeyId: string;
    index: number;
    topic: string;
    jumbles: string[];
    isContentFile: boolean;
    count:number
    gameFormat?: GenieGames;
  }): Observable<IJourney> {
    this._loading = true;
    return this._api
      .post<IJourney>(
        this._mapJumblePath(
          `/genie/jumble/${payload.journeyId}/generateJumble?index=${payload.index}&contentFile=${payload.isContentFile}&count=${payload.count}`,
          payload.gameFormat,
        ),
        {
          jumbles: payload.jumbles,
          topic: payload.topic,
        },
      )
      .pipe(
        map((data: IJourney) => {
          store.update(updateEntities(payload.journeyId, data));
          this._loading = false;
          return data;
        }),
      );
  }
  // moveToLastPage(): void {
  //   let length: number;
  //   let pageSize: number;
  //   let previousPageIndex: number;
  //   this.paginationState$.subscribe((state) => {
  //     length = state.length;
  //     pageSize = state.pageSize;
  //     previousPageIndex = state.previousPageIndex;
  //   });
  //   this.pageChangeEvent({
  //     length,
  //     pageIndex: Math.ceil(length / pageSize),
  //     pageSize,
  //     previousPageIndex,
  //   });
  // }

  // pageChangeEvent(event: PageEvent): void {
  //   store.update((state) => ({
  //     ...state,
  //     length: event.length,
  //     pageIndex: event.pageIndex,
  //     pageSize: event.pageSize,
  //     previousPageIndex: event.previousPageIndex,
  //   }));
  // }

  // setSearchParam(searchParam: string): void {
  //   store.update((state) => ({
  //     ...state,
  //     searchParam: searchParam,
  //   }));
  // }

  // setScrollPosition(scrollPostion: [number, number]): void {
  //   store.update((state) => ({
  //     ...state,
  //     x: scrollPostion[0],
  //     y: scrollPostion[1],
  //   }));
  // }

  // setSelectedId(id: string): void {
  //   store.update((state) => ({
  //     ...state,
  //     selectedId: id,
  //   }));
  // }

  /**
   * Send an invite to a user for a journey
   * @param payload - Contains email, journeyId, companyId, and divisionId
   * @returns Observable with the API response
   */
  public sendInvite(payload: {
    name: string;
    email: string;
    journeyId: string;
    companyId: string;
    divisionId: string;
  }[]): Observable<any> {
    return this._api.post<any>('/genie/journey/sendInvite', payload);
  }

  /**
   * Send bulk invites by uploading an Excel file
   * @param payload - Contains file (FormData) and journeyId
   * @returns Observable with the API response
   */
  public sendBulkInvite(payload: {
    file: File;
    journeyId: string;
    companyId: string;
  }): Observable<any> {
    const formData = new FormData();
    formData.append('file', payload.file);
    return this._http.post(
      `${environment.serverUrl}/genie/journey/bulk-invite?journeyId=${payload.journeyId}&companyId=${payload.companyId}`,
      formData,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getJourneys();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}

interface IJourneyListResponse {
  list: IJourney[];
  totalCount: number;
}
